import { Settings } from "../../../Settings";
import Fast from "../../../util/Fast";
import GridTile from "../../grid/GridTile";
import Path from "../Path";
import PriorityQueue2 from "./PriorityQueue2";

export default class PathFinder {
    constructor() {

        /**
         * @constant
         * @type {number}
         */
        this.openAirTileCost = Settings.tile.size * 15;
        /**
         * @constant
         * @type {number}
         */
        this.closedTileCost = Settings.tile.size * 155;  // Cost penalty for a path going through a closed tile.
        /**
         * @constant
         * @type {number}
         */
        this.gravityDiscount = Settings.tile.halfSize;
        
        // cost to mine the tile

        /**
         * Let's not allow for paths longer than this amount.
         * @constant
         * @type {number}
         */
        this.maxPathDistance = Settings.tile.size * 80; // in pixels

        /**
         * @constant
         * @type {PriorityQueue2}
         */
        this.openList = new PriorityQueue2();
        ////////this.openList = new PriorityQueue();

        /**
         * @type {number}
         */
        this.pathNumber = 0;

        // Do not bother optimizing this. It is calculated only once.
        /**
         * @constant
         * @type {number}
         */
        this.diagonalSize = Math.sqrt(Settings.tile.size * Settings.tile.size + Settings.tile.size * Settings.tile.size) | 0;
    }

    /**
     * @private
     * @param {Path} path 
     * @param {GridTile} startTile 
     * @param {GridTile} endTile 
     * @param {boolean} inclusive 
     * @returns {boolean} Returns true if the path has been handled.
     */
    findPathSpecialCases(path, startTile, endTile, inclusive) {
        //////////////////////////////////////////////
        // Case 1: start == end
        //////////////////////////////////////////////

        if (startTile === endTile) {
            if (inclusive) {
                ////path.push(endTile);
                path.add(endTile);
            }
            return true;
        }

        //////////////////////////////////////////////
        // Case 2: start adjacent to end
        //////////////////////////////////////////////

        if (startTile.isNeighborTile(endTile)) {
            if (inclusive) {
                // path.push(startTile);
                // path.push(endTile);

                path.add(startTile);
                path.add(endTile);
            }
            return true;
        }

        //////////////////////////////////////////////
        // Case 3: start and end are too far apart
        //////////////////////////////////////////////

        const dist = Fast.dist(startTile.origin, endTile.origin);
        if (dist > this.maxPathDistance) {
            console.log('Path too long: ' + (dist / Settings.tile.size) + ' max: ' + (this.maxPathDistance / Settings.tile.size));
            // Don't make the path
            return true;
        }

        return false;
    }

    /**
     * @param {Path} path 
     * @param {GridTile} startTile 
     * @param {GridTile} endTile 
     * @param {boolean} inclusive 
     * @param {boolean} mineThroughWallsEnabled 
     * @param {boolean} ignoreWallPenalty
     */
    findPath(path, startTile, endTile, inclusive, mineThroughWallsEnabled, ignoreWallPenalty) {

        // if (path.length > 0) {
        //     path.length = 0;
        // }

        // Sanity check
        if (!startTile || !endTile) {
            console.log('findPath() null start/end');
            return;
        }

        // See if any of the special cases apply
        if (this.findPathSpecialCases(path, startTile, endTile, inclusive)) {
            return;
        }

        // Increment the path number for each path created.
        this.pathNumber++;

        //const start = Date.now();

        ///////////////////////////////////////////
        // Reset the collections.
        ///////////////////////////////////////////

        this.openList.clear();
        

        ///////////////////////////////////////////
        // Initialize things.
        ///////////////////////////////////////////

        startTile.pathData.setCost(0, this.pathNumber);
        /////////this.openList.add(startTile); // add starting node to open list
        this.openList.add(0, startTile); // add starting node to open list

        startTile.pathData.setInOpenList(true, this.pathNumber);

        let current;
        while (!this.openList.isEmpty()) {
            current = this.openList.poll();

            // current should never be null, but here's a sanity check.
            if (!current) {
                console.log('PathFinder.findPath() current is null. bug');
                break;
            }

            // current has been removed from open list.
            current.pathData.setInOpenList(false, this.pathNumber);

            // See if we have found the goal.
            if (current === endTile) {
                this.constructPath(path, endTile, inclusive);
                break;
            }
            else {
                // We do not need to check the current node again.
                current.pathData.setClosed(true, this.pathNumber);

                const costToCurrent = current.pathData.getCost(this.pathNumber);

                // We need to know if the current tile has an open tile below it so that we
                // know whether gravity would be applied to the character if they were there.
                const neighborBottom = current.getNeighborBottom();
                const currentTileOpenAir = (!neighborBottom || neighborBottom.isTraversable());

                const neighbors = current.getNeighbors();
                for (let i = 0; i < neighbors.length; i++) {
                    const neighbor = neighbors[i];

                    // It's fine for neighbor tiles to be null
                    if (!neighbor) {
                        continue;
                    }

                    // // Only reject non-traversable tiles if they are not the end tile.
                    // if (neighbor !== endTile && !neighbor.isTraversable() && !mineThroughWallsEnabled) {
                    //     continue;
                    //     // future versions could include instructions to mine tiles that are blocking the path.
                    // }

                    // Ignore nodes that we have already checked.
                    if (neighbor.pathData.isClosed(this.pathNumber)) {
                        continue;
                    }

                    // From GridTile
                    // const NEIGHBOR_TOP_INDEX = 0;
                    // const NEIGHBOR_BOTTOM_INDEX = 1;
                    // const NEIGHBOR_RIGHT_INDEX = 2;
                    // const NEIGHBOR_LEFT_INDEX = 3;

                    // If current tile is an open air tile:
                    // neighbor is left/right: open air tile cost
                    // neighbor is above: open air tile cost
                    // neighbor is down: gravity discount maybe

                    let costPenalty = 0;
                    if (currentTileOpenAir) {
                        // i = 1:  this is the bottom neighbor to the current tile.
                        if (i === 1) {
                            costPenalty -= this.gravityDiscount;
                        }
                        else {
                            costPenalty += this.openAirTileCost;
                        }
                    }

                    // Mine Cost
                    if (!neighbor.open && mineThroughWallsEnabled && !ignoreWallPenalty) {
                        costPenalty += this.closedTileCost;
                    }

                    const tentativeCost = costToCurrent + this.distanceBetween(current, neighbor) + costPenalty; // length of this path.

                    const neighborInOpenList = neighbor.pathData.isInOpenList(this.pathNumber);

                    if (neighborInOpenList && tentativeCost >= neighbor.pathData.getCost(this.pathNumber)) {
                        // The new cost to the neighbor is not cheaper than the existing cost to neighbor.
                        continue;
                    }

                    // This path is the best until now.
                    const estimatedScore = tentativeCost + this.calculateHeuristicCostEstimate(neighbor, endTile);
                    neighbor.pathData.set(current, tentativeCost, estimatedScore, this.pathNumber);

                    if (!neighborInOpenList) {
                        ///////////////this.openList.add(neighbor, this.pathNumber);
                        this.openList.add(estimatedScore, neighbor);
            
                        neighbor.pathData.setInOpenList(true, this.pathNumber);
                    }
                }
            }
        }
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {GridTile} endTile 
     * @returns {number}
     */
    calculateHeuristicCostEstimate(tile, endTile) {
        return Fast.dist(tile.getOrigin(), endTile.getOrigin()) | 0;
    }

    /**
     * @private
     * @param {GridTile} current 
     * @param {GridTile} neighbor 
     * @returns {number}
     */
    distanceBetween(current, neighbor) {
        const currentOrigin = current.getOrigin();
        const neighborOrigin = neighbor.getOrigin();

        if (currentOrigin.x == neighborOrigin.x) {
            return Settings.tile.size;
        }
        else if (currentOrigin.y == neighborOrigin.y) {
            return Settings.tile.size;
        }
        else {
            return this.diagonalSize;
        }
    }

    /**
     * @private
     * @param {Path} path 
     * @param {GridTile} endTile 
     * @param {boolean} inclusive 
     */
    constructPath(path, endTile, inclusive) {

        let current;
        if (inclusive) {
            current = endTile;
        }
        else {
            current = endTile.pathData.getCameFrom(this.pathNumber);

            if (current == null) {
                return;
            }
        }

        // We add the tiles in reverse order and this is a special case thing.
        path.addToFrontStart();

        do {
            //////path.unshift(current); // Add to beginning of array
            path.addToFront(current);

            current = current.pathData.getCameFrom(this.pathNumber);
        }
        while (current != null);

        path.addToFrontEnd();
    }

}